<template>
  <template v-if="hasAccess">
    <template v-if="item.type === 'download'">
      <MenuListItem
        :title="$t(`crudmanager.customMenu.${item.label}`)"
        :icon="item.icon"
        :icon-class="'icon-instance'"
        :loading="isDownloading"
        :custom-classes="['instance-menu-item', 'is-download-item', 'text-weight-medium', { 'is-downloading': isDownloading }]"
        :title-class="'text-capitalize text-weight-medium'"
        @click="triggerDownload" />
    </template>

    <template v-else-if="item.type === 'link'">
      <MenuListItem
        :title="$t(`crudmanager.customMenu.${item.label}`)"
        :to="item.to"
        :icon="item.icon"
        :icon-class="'icon-instance'"
        :custom-classes="['instance-menu-item', 'text-weight-medium']"
        :title-class="'text-capitalize text-weight-medium'" />
    </template>

    <MenuListItem
      v-else
      :title="$t(`crudmanager.customMenu.${item.label}`)"
      :icon="item.icon"
      :icon-class="'icon-instance'"
      :custom-classes="['instance-menu-item', 'text-weight-medium']"
      :title-class="'text-capitalize text-weight-medium'">
      <q-menu>
        <q-list>
          <CustomMenuItem
            v-for="child in item.childs"
            :key="child.id"
            :item="child" />
        </q-list>
      </q-menu>
    </MenuListItem>
  </template>
</template>

<script setup lang="ts">
import type { CustomMenuItem } from 'src/CrudManager/config/customMenuItems'
import MenuListItem from 'src/CrudManager/Core/composites/Layout/MenuListItem.vue'
import { useAppSettingsStore } from 'src/CrudManager/Core/stores/useAppSettingsStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { downloadReport } from 'src/CrudManager/Core/Utils/downloadReport'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

defineOptions({ name: 'CustomMenuItem' })

const props = defineProps<{
  item: CustomMenuItem
}>()

const userStore = useUserStore()
const appSettingsStore = useAppSettingsStore()
const { t } = useI18n()

const isDownloading = ref(false)

const triggerDownload = async () => {
  if (!props.item.downloadUrl || isDownloading.value) return
  isDownloading.value = true
  try {
    await downloadReport({
      url: props.item.downloadUrl,
      fallbackFilename: props.item.filename,
      successMessage: t('crudmanager.customMenu.download_success'),
      errorMessage: t('crudmanager.customMenu.download_error'),
      progressMessage: t('crudmanager.customMenu.download_progress'),
    })
  } finally {
    isDownloading.value = false
  }
}

const hasAccess = computed(() => {
  return checkAccess(props.item)
})

const checkAccess = (menuItem: CustomMenuItem) => {
  if (menuItem.companyIds?.length) {
    const currentId = appSettingsStore.currentCompany?.id
    if (!currentId || !menuItem.companyIds.includes(currentId)) {
      return false
    }
  }
  if (menuItem.roles?.length) {
    return menuItem.roles.some((role) => userStore.currentUserHasRole(role))
  }
  if (menuItem.permissions?.length) {
    return menuItem.permissions.some((permission) => userStore.currentUserCan(permission))
  }
  return true
}
</script>

<style lang="scss" scoped>
.instance-menu-item {
  transition: background-color 0.3s ease;
  position: relative;

  &.is-download-item:not(.is-downloading) {
    :deep(.icon-instance) {
      transition:
        transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
        color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover :deep(.icon-instance) {
      transform: translateY(2px);
      color: var(--q-primary);
    }
  }

  &.is-downloading {
    pointer-events: none;
    cursor: progress;
    background-color: color-mix(in srgb, var(--q-primary) 14%, transparent);
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--q-primary) 40%, transparent);

    :deep(.q-item-label):not(.text-caption) {
      color: var(--q-primary);
      font-weight: 550;
    }

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 3px;
      border-radius: 0 0 8px 8px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        var(--q-primary) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: menu-item-progress 1s linear infinite;
    }
  }

  &.q-router-link--active {
    background-color: color-mix(in srgb, var(--q-primary) 28%, transparent) !important;
    color: #ffffff !important;

    &::before {
      content: '';
      position: absolute;
      left: -4px;
      top: 20%;
      bottom: 20%;
      width: 3px;
      border-radius: 2px;
      background: var(--q-primary);
    }

    :deep(.icon-instance),
    :deep(.q-item__label):not(.text-caption) {
      color: #ffffff !important;
      font-weight: 550;
    }
  }
}

.body--light .instance-menu-item.q-router-link--active {
  background-color: color-mix(in srgb, var(--q-primary) 14%, transparent) !important;
  color: color-mix(in srgb, var(--q-primary) 38%, #000) !important;

  :deep(.icon-instance) {
    color: var(--q-primary) !important;
  }

  :deep(.q-item__label):not(.text-caption) {
    color: color-mix(in srgb, var(--q-primary) 38%, #000) !important;
    font-weight: 600;
  }
}


.icon-instance {
  border: none;
  padding: 0;
  border-radius: 0;
  opacity: 0.7;
  font-size: 1.15rem;
  transition:
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &.border-blue {
    color: var(--q-primary);
    opacity: 1;
  }
}

:deep(.q-item:hover .icon-instance),
:deep(.q-item--active .icon-instance) {
  opacity: 1;
}

@keyframes menu-item-progress {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
